import { useCallback, useMemo, useRef } from 'react';
import { parseLocalApiOverride } from '../../../utils/parseLocalApiOverride';
import { isOAuth, isOAuthWithPolling } from '../../../utils/oauthHelpers';
import { useLowLevelAuthFlowMachine } from '../LowLevelAuthFlowMachineContext';
import { useOAuthWindow } from './useOAuthWindow';
import { useSessionPolling } from './useSessionPolling';
const MAX_POLLING_COUNT_FOR_LOADING = 60;
const isConvertibleAuthError = error => typeof error === 'object' && error !== null && 'toAuthError' in error && typeof error.toAuthError === 'function';
export function useFinishAuth() {
  const {
    state,
    send
  } = useLowLevelAuthFlowMachine();
  const {
    portalId,
    authConfig,
    params,
    authProcessStatus,
    pollingCount,
    oAuthState
  } = state.context;
  const {
    appName,
    appId,
    authFlow,
    mcpParams,
    oAuthScopeSet,
    generateOAuthorStateParam,
    buildAuthPayload,
    makeAuthorizeUrlDescriptor,
    finishAuthFlow
  } = authConfig;
  const {
    sessionId,
    type: authFlowType,
    steps
  } = authFlow;
  const handleFailure = useCallback(error => {
    send({
      type: 'FINISH_FAILURE',
      error
    });
  }, [send]);
  const handleSuccess = useCallback(response => {
    send({
      type: 'FINISH_SUCCESS',
      response
    });
  }, [send]);
  const executeAuthFlow = useCallback(async authContext => {
    try {
      const payload = await buildAuthPayload(authContext);
      const response = await finishAuthFlow(payload);
      handleSuccess(response);
    } catch (err) {
      var _error$responseJSON;
      const error = err;
      if (isConvertibleAuthError(err)) {
        handleFailure(err.toAuthError());
      } else if ((_error$responseJSON = error.responseJSON) !== null && _error$responseJSON !== void 0 && _error$responseJSON.message) {
        handleFailure({
          type: 'AuthenticateError',
          message: error.responseJSON.message
        });
      } else {
        handleFailure({
          type: 'AuthenticateError',
          message: error.message
        });
      }
    }
  }, [buildAuthPayload, finishAuthFlow, handleSuccess, handleFailure]);
  const isFinishingRef = useRef(false);
  const handleOAuthResponse = useCallback((oAuthParams, receivedState) => {
    if (receivedState !== oAuthState) {
      handleFailure({
        type: 'UnknownOAuthProblem'
      });
      return;
    }

    // Guards against both the window-message and polling paths firing
    // simultaneously. Reset happens in startAuth on each new attempt.
    if (isFinishingRef.current) {
      return;
    }
    isFinishingRef.current = true;
    send({
      type: 'OAUTH_RESPONSE_RECEIVED',
      params: oAuthParams,
      state: receivedState
    });
    void executeAuthFlow({
      sessionId,
      steps,
      urlParams: params,
      oAuthParams,
      oAuthState: receivedState,
      oAuthScopeSet,
      mcpParams,
      appId
    });
  }, [oAuthState, sessionId, steps, params, oAuthScopeSet, mcpParams, appId, executeAuthFlow, handleFailure, send]);
  const oAuthCallbacks = useMemo(() => ({
    onWindowOpened: (oAuthWindow, oAuthStateValue) => {
      send({
        type: 'OAUTH_WINDOW_OPENED',
        window: oAuthWindow,
        oAuthState: oAuthStateValue
      });
      if (isOAuthWithPolling(authFlowType)) {
        send({
          type: 'START_POLLING'
        });
      }
    },
    onWindowBlocked: () => {
      send({
        type: 'OAUTH_WINDOW_BLOCKED'
      });
    },
    onWindowClosed: () => {
      send({
        type: 'CANCEL_AUTH',
        reason: 'window_closed'
      });
    },
    onWindowTimeout: () => {
      send({
        type: 'OAUTH_WINDOW_TIMEOUT'
      });
    },
    onAuthResponse: (oAuthParams, status) => {
      if (status === 'AUTH_RESPONSE') {
        const receivedState = oAuthParams.get('state') || '';
        handleOAuthResponse(oAuthParams, receivedState);
      }
    },
    onAuthError: message => {
      handleFailure({
        type: 'AuthorizeError',
        message
      });
    }
  }), [authFlowType, handleFailure, handleOAuthResponse, send]);
  const {
    openWindow
  } = useOAuthWindow({
    appName,
    authFlowType,
    callbacks: oAuthCallbacks,
    enabled: authProcessStatus === 'authorizing' || authProcessStatus === 'polling'
  });
  const pollingCallbacks = useMemo(() => ({
    onPollTick: () => {
      send({
        type: 'POLL_TICK'
      });
    },
    onPollSuccess: (pollParams, status) => {
      const stateParam = pollParams.get('state') || sessionId;
      send({
        type: 'POLL_SUCCESS',
        params: pollParams,
        status
      });
      if (status !== 'PENDING') {
        handleOAuthResponse(pollParams, stateParam);
      }
    },
    onPollError: () => {
      send({
        type: 'POLL_ERROR'
      });
    },
    onTimeout: () => {
      send({
        type: 'OAUTH_WINDOW_TIMEOUT'
      });
    }
  }), [handleOAuthResponse, send, sessionId]);
  useSessionPolling({
    portalId,
    sessionId,
    callbacks: pollingCallbacks,
    enabled: authProcessStatus === 'polling'
  });
  const startAuth = useCallback(() => {
    isFinishingRef.current = false;
    send({
      type: 'START_AUTH'
    });
    if (isOAuth(authFlowType)) {
      const executeOAuthFlow = async () => {
        try {
          const generatedState = generateOAuthorStateParam ? await generateOAuthorStateParam({
            sessionId,
            steps,
            urlParams: params,
            oAuthScopeSet,
            mcpParams,
            appId
          }) : sessionId;
          const authPayload = await buildAuthPayload({
            sessionId,
            steps,
            urlParams: params,
            oAuthScopeSet,
            oAuthState: generatedState,
            oAuthParams: new Map(),
            mcpParams,
            appId
          });
          const {
            authorizeUrl
          } = await makeAuthorizeUrlDescriptor(authPayload);
          const parsedUrl = parseLocalApiOverride(authorizeUrl);
          openWindow(generatedState, parsedUrl);
        } catch (err) {
          const error = err;
          handleFailure({
            type: 'AuthorizeError',
            message: error.message
          });
        }
      };
      void executeOAuthFlow();
    } else {
      void executeAuthFlow({
        sessionId,
        steps,
        urlParams: params,
        appId
      });
    }
  }, [send, authFlowType, sessionId, steps, params, oAuthScopeSet, mcpParams, appId, generateOAuthorStateParam, buildAuthPayload, makeAuthorizeUrlDescriptor, openWindow, executeAuthFlow, handleFailure]);
  const isLoading = authProcessStatus === 'authorizing' || authProcessStatus === 'authenticating' || authProcessStatus === 'polling' && pollingCount < MAX_POLLING_COUNT_FOR_LOADING;
  const hasFailed = authProcessStatus === 'failed' || authProcessStatus === 'cancelled' || authProcessStatus === 'polling' && pollingCount >= MAX_POLLING_COUNT_FOR_LOADING;
  return {
    startAuth,
    isLoading,
    hasFailed
  };
}